import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MoreVertical,
  BarChart2,
  Eye,
  Pencil,
  Share2,
  Copy,
  BookmarkPlus,
  Lock,
  Archive,
  Download,
  Trash2,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useCloseForm, useArchiveForm, useDeleteForm } from '../../hooks/useFormLifecycleMutations';
import { useDuplicateForm } from '../../hooks/useFormMutations';
import { useExportSubmissions } from '../../hooks/useExportSubmissions';
import { ShareFormDialog } from '../ShareFormDialog';
import { SaveAsTemplateDialog } from '../Templates/SaveAsTemplateDialog';
import { FormPreviewDialog } from '../FormRenderer/FormPreviewDialog';
import type { FormSummary } from '../../types';

interface FormRowActionsMenuProps {
  form: FormSummary;
}

export function FormRowActionsMenu({ form }: FormRowActionsMenuProps) {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const [shareOpen, setShareOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const closeForm = useCloseForm(form.id);
  const archiveForm = useArchiveForm(form.id);
  const deleteForm = useDeleteForm(form.id);
  const duplicateForm = useDuplicateForm(form.id);
  const exportSubmissions = useExportSubmissions(form.id, form.title);
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();

  async function handleClose() {
    const { confirmed } = await confirm({
      title: t('actions.confirmCloseTitle'),
      description: t('actions.confirmCloseDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await closeForm.mutateAsync();
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleArchive() {
    const { confirmed } = await confirm({
      title: t('actions.confirmArchiveTitle'),
      description: t('actions.confirmArchiveDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await archiveForm.mutateAsync();
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleDelete() {
    const { confirmed } = await confirm({
      title: t('actions.confirmDeleteTitle'),
      description: t('actions.confirmDeleteDescription'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await deleteForm.mutateAsync();
    } catch {
      // error handled by global handler in bootstrap.ts (e.g. Form.HasSubmissions)
    }
  }

  async function handleDuplicate() {
    try {
      const { data: created } = await duplicateForm.mutateAsync(undefined);
      navigate(`/${orgId}/forms/${created.id}`);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('list.table.actions')}
                data-testid={`forms-row-actions-menu-${form.id}`}
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('list.table.actions')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => navigate(`/${orgId}/forms/${form.id}?tab=submissions`)}>
            <BarChart2 className="h-4 w-4 mr-2" />
            {t('list.rowActions.viewSubmissions')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setPreviewOpen(true)}>
            <Eye className="h-4 w-4 mr-2" />
            {t('list.rowActions.preview')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(`/${orgId}/forms/${form.id}`)}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('list.rowActions.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate} disabled={duplicateForm.isPending}>
            <Copy className="h-4 w-4 mr-2" />
            {t('list.rowActions.duplicate')}
          </DropdownMenuItem>
          {form.status !== 'Archived' && (
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" />
              {t('list.rowActions.share')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setSaveAsTemplateOpen(true)}>
            <BookmarkPlus className="h-4 w-4 mr-2" />
            {t('list.rowActions.saveAsTemplate')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => exportSubmissions.mutate()}
            disabled={exportSubmissions.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            {t('list.rowActions.exportCsv')}
          </DropdownMenuItem>
          {form.status === 'Shared' && (
            <DropdownMenuItem onClick={handleClose} disabled={closeForm.isPending}>
              <Lock className="h-4 w-4 mr-2" />
              {t('list.rowActions.close')}
            </DropdownMenuItem>
          )}
          {form.status !== 'Archived' && (
            <DropdownMenuItem onClick={handleArchive} disabled={archiveForm.isPending}>
              <Archive className="h-4 w-4 mr-2" />
              {t('list.rowActions.archive')}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteForm.isPending}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t('list.rowActions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ShareFormDialog
        formId={form.id}
        projectId={form.projectId}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />

      <SaveAsTemplateDialog
        formId={form.id}
        formTitle={form.title}
        open={saveAsTemplateOpen}
        onOpenChange={setSaveAsTemplateOpen}
      />

      <FormPreviewDialog formId={form.id} open={previewOpen} onOpenChange={setPreviewOpen} />

      <ConfirmDialog
        open={isOpen}
        onOpenChange={(v) => {
          if (!v) handleCancel();
        }}
        title={options?.title ?? ''}
        description={options?.description}
        variant={options?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </>
  );
}
